/**
 * WorkspaceSkills - Skills implementation.
 *
 * Provides discovery and search operations for skills stored
 * in skills paths. All operations are async.
 */

import matter from 'gray-matter';

import { isGlobPattern, resolvePathPattern } from '../glob';
import type { ReaddirEntry } from '../glob';
import type { IndexDocument, SearchResult } from '../search';
import { validateSkillMetadata } from './schemas';
import type { SkillSource as SkillSourceInterface } from './skill-source';
import type {
  ContentSource,
  Skill,
  SkillMetadata,
  SkillSearchResult,
  SkillSearchOptions,
  WorkspaceSkills,
  SkillsResolver,
  SkillsContext,
} from './types';

// =============================================================================
// Internal Types
// =============================================================================

/**
 * Minimal search engine interface - only the methods we actually use.
 * This allows both the real SearchEngine and test mocks to be used.
 */
interface SkillSearchEngine {
  index(doc: IndexDocument): Promise<void>;
  remove?(id: string): Promise<void>;
  search(
    query: string,
    options?: { topK?: number; minScore?: number; mode?: 'bm25' | 'vector' | 'hybrid' },
  ): Promise<SearchResult[]>;
  clear(): void;
}

interface InternalSkill extends Skill {
  /** Content for BM25 indexing (instructions + all references) */
  indexableContent: string;
}

// =============================================================================
// WorkspaceSkillsImpl
// =============================================================================

/**
 * Configuration for WorkspaceSkillsImpl
 */
export interface WorkspaceSkillsImplConfig {
  /**
   * Source for loading skills.
   */
  source: SkillSourceInterface;
  /**
   * Paths to scan for skills.
   * Can be a static array or a function that returns paths based on context.
   */
  skills: SkillsResolver;
  /** Search engine for skill search (optional) */
  searchEngine?: SkillSearchEngine;
  /** Validate skills on load (default: true) */
  validateOnLoad?: boolean;
  /**
   * Check SKILL.md file mtime in addition to directory mtime for staleness detection.
   * Enables detection of in-place file edits (e.g., fixing validation errors).
   * Increases stat calls - recommended for local development only.
   * Default: false
   */
  checkSkillFileMtime?: boolean;
}

/**
 * Implementation of WorkspaceSkills interface.
 */
export class WorkspaceSkillsImpl implements WorkspaceSkills {
  readonly #source: SkillSourceInterface;
  readonly #skillsResolver: SkillsResolver;
  readonly #searchEngine?: SkillSearchEngine;
  readonly #validateOnLoad: boolean;
  readonly #checkSkillFileMtime: boolean;

  /** Map of skill name -> array of candidates (supports same-named skills from different sources) */
  #skills: Map<string, InternalSkill[]> = new Map();

  /** Whether skills have been discovered */
  #initialized = false;

  /** Promise for ongoing initialization (prevents concurrent discovery) */
  #initPromise: Promise<void> | null = null;

  /** Timestamp of last skills discovery (for staleness check) */
  #lastDiscoveryTime = 0;

  /** Currently resolved skills paths (used to detect changes) */
  #resolvedPaths: string[] = [];

  /** Cached glob-resolved directories and per-pattern resolve timestamps */
  #globDirCache: Map<string, string[]> = new Map();
  #globResolveTimes: Map<string, number> = new Map();
  static readonly GLOB_RESOLVE_INTERVAL = 5_000; // Re-walk glob dirs every 5s
  static readonly STALENESS_CHECK_COOLDOWN = 2_000; // Skip staleness check for 2s after discovery

  constructor(config: WorkspaceSkillsImplConfig) {
    this.#source = config.source;
    this.#skillsResolver = config.skills;
    this.#searchEngine = config.searchEngine;
    this.#validateOnLoad = config.validateOnLoad ?? true;
    this.#checkSkillFileMtime = config.checkSkillFileMtime ?? false;
  }

  // ===========================================================================
  // Discovery
  // ===========================================================================

  async list(): Promise<SkillMetadata[]> {
    await this.#ensureInitialized();

    const results: SkillMetadata[] = [];
    for (const candidates of this.#skills.values()) {
      const canonicalCandidates = await this.#dedupeCanonicalCandidates(candidates);
      for (const skill of canonicalCandidates) {
        results.push({
          name: skill.name,
          path: skill.path,
          description: skill.description,
          license: skill.license,
          compatibility: skill.compatibility,
          'user-invocable': skill['user-invocable'],
          metadata: skill.metadata,
        });
      }
    }
    return results;
  }

  async get(name: string): Promise<Skill | null> {
    await this.#ensureInitialized();
    // Try name-based lookup first, then fall back to path-based (escape hatch)
    const skill = (await this.#resolveByName(name)) ?? this.#resolveByPath(name);
    if (!skill) return null;

    // Return without internal indexableContent field
    const { indexableContent: _, ...skillData } = skill;
    return skillData;
  }

  async has(name: string): Promise<boolean> {
    await this.#ensureInitialized();
    return ((await this.#resolveByName(name)) ?? this.#resolveByPath(name)) !== null;
  }

  // ===========================================================================
  // Skill Resolution (Private)
  // ===========================================================================

  /**
   * Resolve a skill by name with tie-breaking when multiple candidates exist.
   * Priority: local > managed > external, then alphabetical path.
   */
  async #resolveByName(name: string): Promise<InternalSkill | null> {
    const candidates = this.#skills.get(name);
    if (!candidates || candidates.length === 0) return null;
    return this.#tieBreak(candidates);
  }

  /**
   * Resolve a skill by exact path (escape hatch for disambiguation).
   * Searches across all candidate arrays.
   * Accepts paths with or without a trailing `/SKILL.md` suffix, since
   * SkillsProcessor.formatLocation() exposes `${path}/SKILL.md` to the LLM.
   */
  #resolveByPath(skillPath: string): InternalSkill | null {
    const normalized = skillPath.replace(/\/SKILL\.md$/, '');
    for (const candidates of this.#skills.values()) {
      const match = candidates.find(s => s.path === normalized);
      if (match) return match;
    }
    return null;
  }

  async #getCanonicalSkillPath(skillPath: string): Promise<string> {
    if (!this.#source.realpath) return skillPath;

    try {
      return await this.#source.realpath(skillPath);
    } catch {
      return skillPath;
    }
  }

  async #dedupeCanonicalCandidates(candidates: InternalSkill[]): Promise<InternalSkill[]> {
    const canonicalGroups = new Map<string, InternalSkill[]>();
    for (const candidate of candidates) {
      const canonicalPath = await this.#getCanonicalSkillPath(candidate.path);
      const group = canonicalGroups.get(canonicalPath) ?? [];
      group.push(candidate);
      canonicalGroups.set(canonicalPath, group);
    }

    const SOURCE_PRIORITY: Record<string, number> = { local: 0, managed: 1, external: 2 };
    return [...canonicalGroups.values()].map(
      group =>
        [...group].sort((a, b) => {
          const aPri = SOURCE_PRIORITY[a.source.type] ?? 99;
          const bPri = SOURCE_PRIORITY[b.source.type] ?? 99;
          if (aPri !== bPri) return aPri - bPri;
          return a.path.localeCompare(b.path);
        })[0]!,
    );
  }

  /**
   * Pick the winning skill from an array of same-named candidates.
   * When there's only one candidate, returns it directly (no warning).
   * When there are multiple, de-duplicates alias paths that point to the same
   * canonical skill, then applies source-type priority and warns.
   *
   * Priority: local (0) > managed (1) > external (2).
   * Throws if source-type priority can't resolve the tie (e.g., two distinct local skills with same name).
   */
  async #tieBreak(candidates: InternalSkill[]): Promise<InternalSkill | null> {
    if (candidates.length === 0) return null;
    if (candidates.length === 1) return candidates[0]!;

    const deduped = await this.#dedupeCanonicalCandidates(candidates);

    if (deduped.length === 1) return deduped[0]!;

    const SOURCE_PRIORITY: Record<string, number> = { local: 0, managed: 1, external: 2 };
    const sorted = [...deduped].sort((a, b) => {
      const aPri = SOURCE_PRIORITY[a.source.type] ?? 99;
      const bPri = SOURCE_PRIORITY[b.source.type] ?? 99;
      if (aPri !== bPri) return aPri - bPri;
      return a.path.localeCompare(b.path);
    });

    const winner = sorted[0]!;
    const runnerUp = sorted[1]!;

    // Error if source-type priority can't break the tie
    if (winner.source.type === runnerUp.source.type) {
      const paths = sorted
        .filter(s => s.source.type === winner.source.type)
        .map(s => `"${s.path}"`)
        .join(', ');
      throw new Error(
        `[WorkspaceSkills] Cannot resolve skill "${winner.name}": multiple ${winner.source.type} skills found at ${paths}. ` +
          `Rename one or move it to a different source type.`,
      );
    }

    console.warn(
      `[WorkspaceSkills] Multiple skills named "${winner.name}" found. ` +
        `Using "${winner.path}" (source: ${winner.source.type}). ` +
        `Other candidates: ${sorted
          .slice(1)
          .map(s => `"${s.path}" (${s.source.type})`)
          .join(', ')}`,
    );

    return winner;
  }

  async refresh(): Promise<void> {
    // Remove only skill entries from the shared search engine (not workspace content)
    for (const candidates of this.#skills.values()) {
      for (const skill of candidates) {
        await this.#removeSkillFromIndex(skill);
      }
    }
    this.#skills.clear();
    this.#initialized = false;
    this.#initPromise = null;
    await this.#discoverSkills();
    this.#initialized = true;
  }

  async maybeRefresh(context?: SkillsContext): Promise<void> {
    // Ensure initial discovery is complete
    await this.#ensureInitialized();

    // Resolve current paths (may be dynamic based on context)
    const currentPaths = await this.#resolvePaths(context);

    // Check if paths have changed (for dynamic resolvers)
    const pathsChanged = !this.#arePathsEqual(this.#resolvedPaths, currentPaths);
    if (pathsChanged) {
      // Paths changed - need full refresh with new paths
      this.#resolvedPaths = currentPaths;
      await this.refresh();
      return;
    }

    // Check if any skills path has been modified since last discovery
    const isStale = await this.#isSkillsPathStale();
    if (isStale) {
      await this.refresh();
    }
  }

  async addSkill(skillPath: string): Promise<void> {
    await this.#ensureInitialized();

    // Determine SKILL.md path and dirName
    let skillFilePath: string;
    let dirName: string;
    if (skillPath.endsWith('/SKILL.md') || skillPath === 'SKILL.md') {
      skillFilePath = skillPath;
      dirName = this.#getParentPath(skillPath).split('/').pop() || 'unknown';
    } else {
      skillFilePath = this.#joinPath(skillPath, 'SKILL.md');
      dirName = skillPath.split('/').pop() || 'unknown';
    }

    // Determine source from existing resolved paths
    const source = this.#inferSource(skillPath);

    // Parse and add to cache
    const skill = await this.#parseSkillFile(skillFilePath, dirName, source);

    // Remove old index entries if skill already exists at same path (for update case)
    const candidates = this.#skills.get(skill.name) ?? [];
    const existingIdx = candidates.findIndex(s => s.path === skill.path);
    if (existingIdx >= 0) {
      await this.#removeSkillFromIndex(candidates[existingIdx]!);
      candidates[existingIdx] = skill;
    } else {
      candidates.push(skill);
    }
    this.#skills.set(skill.name, candidates);
    await this.#indexSkill(skill);

    // Update discovery time so maybeRefresh() doesn't trigger full scan
    this.#lastDiscoveryTime = Date.now();
  }

  async removeSkill(skillName: string): Promise<void> {
    await this.#ensureInitialized();

    // Resolve by name (tie-break winner), then fall back to path-based lookup
    const skill = (await this.#resolveByName(skillName)) ?? this.#resolveByPath(skillName);
    if (!skill) return;

    // Remove from search index
    await this.#removeSkillFromIndex(skill);

    // Remove from candidates array
    const candidates = this.#skills.get(skill.name);
    if (candidates) {
      const idx = candidates.findIndex(s => s.path === skill.path);
      if (idx >= 0) candidates.splice(idx, 1);
      if (candidates.length === 0) {
        this.#skills.delete(skill.name);
      }
    }

    // Update discovery time so maybeRefresh() doesn't trigger full scan
    this.#lastDiscoveryTime = Date.now();
  }

  /**
   * Resolve skills paths from the resolver (static array or function).
   */
  async #resolvePaths(context?: SkillsContext): Promise<string[]> {
    if (Array.isArray(this.#skillsResolver)) {
      return this.#skillsResolver;
    }
    return this.#skillsResolver(context ?? {});
  }

  /**
   * Compare two path arrays for equality (order-independent).
   */
  #arePathsEqual(a: string[], b: string[]): boolean {
    if (a.length !== b.length) return false;
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((path, i) => path === sortedB[i]);
  }

  // ===========================================================================
  // Search
  // ===========================================================================

  async search(query: string, options: SkillSearchOptions = {}): Promise<SkillSearchResult[]> {
    await this.#ensureInitialized();

    if (!this.#searchEngine) {
      // Fall back to simple text matching if no search engine
      return this.#simpleSearch(query, options);
    }

    const { topK = 5, minScore, skillNames, includeReferences = true, mode } = options;

    // Ask the search engine for enough rows to survive post-search filtering and
    // canonical alias de-duplication before applying the final topK.
    const totalIndexedDocuments = [...this.#skills.values()].reduce(
      (count, candidates) =>
        count + candidates.reduce((skillCount, skill) => skillCount + 1 + skill.references.length, 0),
      0,
    );
    const expandedTopK = Math.max(skillNames ? topK * 3 : topK, totalIndexedDocuments);

    // Delegate to SearchEngine
    const searchResults = await this.#searchEngine.search(query, {
      topK: expandedTopK,
      minScore,
      mode,
    });

    const results: SkillSearchResult[] = [];
    const seenCanonicalSources = new Set<string>();

    for (const result of searchResults) {
      const skillPath = result.metadata?.skillPath as string;
      const source = result.metadata?.source as string;

      if (!skillPath || !source) continue;

      // Map path back to the canonical skill winner for filtering and results.
      const matchedSkill = this.#resolveByPath(skillPath);
      if (!matchedSkill) continue;

      const skill = (await this.#resolveByName(matchedSkill.name)) ?? matchedSkill;

      // Filter by skill names if specified
      if (skillNames && !skillNames.includes(skill.name)) {
        continue;
      }

      // Filter out references if not included
      if (!includeReferences && source !== 'SKILL.md') {
        continue;
      }

      const canonicalSourceKey = `${skill.path}:${source}`;
      if (seenCanonicalSources.has(canonicalSourceKey)) {
        continue;
      }
      seenCanonicalSources.add(canonicalSourceKey);

      results.push({
        skillName: skill.name,
        skillPath: skill.path,
        source,
        content: result.content,
        score: result.score,
        lineRange: result.lineRange,
        scoreDetails: result.scoreDetails,
      });

      if (results.length >= topK) break;
    }

    return results;
  }

  // ===========================================================================
  // Single-item Accessors
  // ===========================================================================

  async getReference(skillName: string, referencePath: string): Promise<string | null> {
    await this.#ensureInitialized();

    const skill = (await this.#resolveByName(skillName)) ?? this.#resolveByPath(skillName);
    if (!skill) return null;

    const safeRefPath = this.#assertRelativePath(referencePath, 'reference');
    const refFilePath = this.#joinPath(skill.path, safeRefPath);

    if (!(await this.#source.exists(refFilePath))) {
      return null;
    }

    try {
      const content = await this.#source.readFile(refFilePath);
      return typeof content === 'string' ? content : content.toString('utf-8');
    } catch {
      return null;
    }
  }

  async getScript(skillName: string, scriptPath: string): Promise<string | null> {
    await this.#ensureInitialized();

    const skill = (await this.#resolveByName(skillName)) ?? this.#resolveByPath(skillName);
    if (!skill) return null;

    const safeScriptPath = this.#assertRelativePath(scriptPath, 'script');
    const scriptFilePath = this.#joinPath(skill.path, safeScriptPath);

    if (!(await this.#source.exists(scriptFilePath))) {
      return null;
    }

    try {
      const content = await this.#source.readFile(scriptFilePath);
      return typeof content === 'string' ? content : content.toString('utf-8');
    } catch {
      return null;
    }
  }

  async getAsset(skillName: string, assetPath: string): Promise<Buffer | null> {
    await this.#ensureInitialized();

    const skill = (await this.#resolveByName(skillName)) ?? this.#resolveByPath(skillName);
    if (!skill) return null;

    const safeAssetPath = this.#assertRelativePath(assetPath, 'asset');
    const assetFilePath = this.#joinPath(skill.path, safeAssetPath);

    if (!(await this.#source.exists(assetFilePath))) {
      return null;
    }

    try {
      const content = await this.#source.readFile(assetFilePath);
      return typeof content === 'string' ? Buffer.from(content, 'utf-8') : content;
    } catch {
      return null;
    }
  }

  // ===========================================================================
  // Listing Accessors
  // ===========================================================================

  async listReferences(skillName: string): Promise<string[]> {
    await this.#ensureInitialized();
    const skill = (await this.#resolveByName(skillName)) ?? this.#resolveByPath(skillName);
    return skill?.references ?? [];
  }

  async listScripts(skillName: string): Promise<string[]> {
    await this.#ensureInitialized();
    const skill = (await this.#resolveByName(skillName)) ?? this.#resolveByPath(skillName);
    return skill?.scripts ?? [];
  }

  async listAssets(skillName: string): Promise<string[]> {
    await this.#ensureInitialized();
    const skill = (await this.#resolveByName(skillName)) ?? this.#resolveByPath(skillName);
    return skill?.assets ?? [];
  }

  // ===========================================================================
  // Private Methods
  // ===========================================================================

  /**
   * Ensure skills have been discovered.
   * Uses a promise to prevent concurrent discovery.
   */
  async #ensureInitialized(): Promise<void> {
    if (this.#initialized) {
      return;
    }

    // If initialization is already in progress, wait for it
    if (this.#initPromise) {
      await this.#initPromise;
      return;
    }

    // Start initialization and store the promise
    this.#initPromise = (async () => {
      try {
        // Resolve paths on first initialization (uses empty context)
        if (this.#resolvedPaths.length === 0) {
          this.#resolvedPaths = await this.#resolvePaths();
        }
        await this.#discoverSkills();
        this.#initialized = true;
      } finally {
        this.#initPromise = null;
      }
    })();

    await this.#initPromise;
  }

  /**
   * Add a skill to the candidates map, keyed by name.
   * Replaces an existing entry at the same path (update case), otherwise appends.
   */
  #addToSkillsMap(skill: InternalSkill): void {
    const candidates = this.#skills.get(skill.name) ?? [];
    const idx = candidates.findIndex(s => s.path === skill.path);
    if (idx >= 0) {
      candidates[idx] = skill;
    } else {
      candidates.push(skill);
    }
    this.#skills.set(skill.name, candidates);
  }

  /**
   * Discover skills from all skills paths.
   * Uses currently resolved paths (must be set before calling).
   *
   * Paths can be plain directories, glob patterns, or direct
   * skill references (e.g., '/skills/my-skill/SKILL.md').
   *
   * Uses resolvePathPattern for unified glob resolution. File matches
   * pointing to SKILL.md are loaded directly; directory matches are
   * tried as direct skills first, then scanned for subdirectories.
   */
  async #discoverSkills(): Promise<void> {
    // Clear glob cache so discovery gets fresh results
    this.#globDirCache.clear();
    this.#globResolveTimes.clear();

    // Adapt SkillSource.readdir to the ReaddirEntry interface used by resolvePathPattern
    const readdir = async (dir: string): Promise<ReaddirEntry[]> => {
      const entries = await this.#source.readdir(dir);
      return entries.map(e => ({ name: e.name, type: e.type, isSymlink: e.isSymlink }));
    };

    for (const rawSkillsPath of this.#resolvedPaths) {
      // Strip trailing slash for consistent path handling (e.g. '/skills/' → '/skills')
      const skillsPath =
        rawSkillsPath.length > 1 && rawSkillsPath.endsWith('/') ? rawSkillsPath.slice(0, -1) : rawSkillsPath;
      const source = this.#determineSource(skillsPath);

      if (isGlobPattern(skillsPath)) {
        // Glob pattern: resolve to matching entries, then discover skills from each
        const resolved = await resolvePathPattern(skillsPath, readdir, { dot: true, maxDepth: 4 });

        // Cache directories for staleness checks: matched dirs directly,
        // and parent dirs for matched files (e.g. **/SKILL.md → parent skill dir)
        const dirs = new Set<string>();
        for (const entry of resolved) {
          if (entry.type === 'directory') {
            dirs.add(entry.path);
          } else {
            dirs.add(this.#getParentPath(entry.path));
          }
        }
        this.#globDirCache.set(skillsPath, [...dirs]);
        this.#globResolveTimes.set(skillsPath, Date.now());

        // Process glob-resolved entries in parallel (independent discoveries)
        const results = await Promise.allSettled(
          resolved.map(async entry => {
            if (entry.type === 'file') {
              // File match (e.g., **/SKILL.md) — load as direct skill
              await this.#discoverDirectSkill(entry.path, source);
            } else {
              // Directory match — try as direct skill first, then scan subdirectories
              const isDirect = await this.#discoverDirectSkill(entry.path, source);
              if (!isDirect) {
                await this.#discoverSkillsInPath(entry.path, source);
              }
            }
          }),
        );

        for (const [index, result] of results.entries()) {
          const entry = resolved[index];
          if (entry && result.status === 'rejected') {
            const error = result.reason;
            if (error instanceof Error) {
              console.error(`[WorkspaceSkills] Failed to load skill from ${entry.path}:`, error.message);
            }
          }
        }
      } else {
        // Check if the path is a direct skill reference (directory with SKILL.md or SKILL.md file)
        const isDirect = await this.#discoverDirectSkill(skillsPath, source);
        if (!isDirect) {
          // Plain path: scan subdirectories for skills
          await this.#discoverSkillsInPath(skillsPath, source);
        }
      }
    }
    // Track when discovery completed for staleness check
    this.#lastDiscoveryTime = Date.now();
  }

  /**
   * Discover skills in a single path
   */
  async #discoverSkillsInPath(skillsPath: string, source: ContentSource): Promise<void> {
    try {
      if (!(await this.#source.exists(skillsPath))) {
        return;
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      let hint = '';

      // If an absolute path like "/skills" fails, check if the relative equivalent exists
      if (skillsPath.startsWith('/') && msg.includes('Permission denied')) {
        const relativePath = skillsPath.slice(1);
        try {
          if (await this.#source.exists(relativePath)) {
            hint = ` (did you mean to use the relative path "${relativePath}"?)`;
          }
        } catch {
          // ignore — just skip the hint
        }
      }

      console.warn(`[WorkspaceSkills] Cannot access skills path "${skillsPath}": ${msg}${hint}`);
      return;
    }

    try {
      const entries = await this.#source.readdir(skillsPath);

      // Process all skill directories in parallel (each is independent)
      const results = await Promise.allSettled(
        entries
          .filter(entry => entry.type === 'directory')
          .map(async entry => {
            const entryPath = this.#joinPath(skillsPath, entry.name);
            const skillFilePath = this.#joinPath(entryPath, 'SKILL.md');

            if (await this.#source.exists(skillFilePath)) {
              const skill = await this.#parseSkillFile(skillFilePath, entry.name, source);
              return skill;
            }
            return null;
          }),
      );

      // Apply results sequentially to preserve overwrite semantics
      for (const result of results) {
        if (result.status === 'fulfilled' && result.value) {
          this.#addToSkillsMap(result.value);
          await this.#indexSkill(result.value);
        } else if (result.status === 'rejected') {
          const error = result.reason;
          if (error instanceof Error) {
            console.error(`[WorkspaceSkills] Failed to load skill from ${skillsPath}:`, error.message);
          }
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        console.error(`[WorkspaceSkills] Failed to scan skills directory ${skillsPath}:`, error.message);
      }
    }
  }

  /**
   * Attempt to discover a skill from a direct path reference.
   *
   * Handles two cases:
   * - Path ends with `/SKILL.md` → parse directly, extract dirName from parent
   * - Path is a directory containing `SKILL.md` → parse it as a single skill
   *
   * Returns `true` if the path was a direct skill reference (skip subdirectory scan),
   * `false` to fall through to the normal subdirectory scan.
   */
  async #discoverDirectSkill(skillsPath: string, source: ContentSource): Promise<boolean> {
    try {
      // Case 1: Path points directly to a SKILL.md file
      if (skillsPath.endsWith('/SKILL.md') || skillsPath === 'SKILL.md') {
        if (!(await this.#source.exists(skillsPath))) {
          return true; // It was a direct reference, just doesn't exist — skip subdirectory scan
        }

        const skillDir = this.#getParentPath(skillsPath);
        const dirName = skillDir.split('/').pop() || skillDir;

        try {
          const skill = await this.#parseSkillFile(skillsPath, dirName, source);
          this.#addToSkillsMap(skill);
          await this.#indexSkill(skill);
        } catch (error) {
          if (error instanceof Error) {
            console.error(`[WorkspaceSkills] Failed to load skill from ${skillsPath}:`, error.message);
          }
        }
        return true;
      }

      // Case 2: Path is a directory that directly contains SKILL.md
      if (await this.#source.exists(skillsPath)) {
        const skillFilePath = this.#joinPath(skillsPath, 'SKILL.md');
        if (await this.#source.exists(skillFilePath)) {
          const dirName = skillsPath.split('/').pop() || skillsPath;

          try {
            const skill = await this.#parseSkillFile(skillFilePath, dirName, source);
            this.#addToSkillsMap(skill);
            await this.#indexSkill(skill);
          } catch (error) {
            if (error instanceof Error) {
              console.error(`[WorkspaceSkills] Failed to load skill from ${skillFilePath}:`, error.message);
            }
          }
          return true;
        }
      }

      return false;
    } catch {
      return false;
    }
  }

  /**
   * Check if any skills path directory has been modified since last discovery.
   * Compares directory mtime to lastDiscoveryTime.
   * For glob patterns, checks the walk root and expanded directories.
   */
  async #isSkillsPathStale(): Promise<boolean> {
    if (this.#lastDiscoveryTime === 0) {
      // Never discovered, consider stale
      return true;
    }

    // Skip the expensive stat calls if discovery happened very recently
    // (e.g., right after a surgical addSkill/removeSkill). This avoids
    // a timing race where the filesystem write updates directory mtime
    // to the same second as #lastDiscoveryTime, and also avoids slow
    // stat calls to external mounts immediately after a known-good update.
    if (Date.now() - this.#lastDiscoveryTime < WorkspaceSkillsImpl.STALENESS_CHECK_COOLDOWN) {
      return false;
    }

    for (const skillsPath of this.#resolvedPaths) {
      let pathsToCheck: string[];

      if (isGlobPattern(skillsPath)) {
        // Use cached glob dirs, re-resolve periodically to discover new entries
        const now = Date.now();
        const lastResolved = this.#globResolveTimes.get(skillsPath) ?? 0;
        if (now - lastResolved > WorkspaceSkillsImpl.GLOB_RESOLVE_INTERVAL || !this.#globDirCache.has(skillsPath)) {
          const readdir = async (dir: string): Promise<ReaddirEntry[]> => {
            const entries = await this.#source.readdir(dir);
            return entries.map(e => ({ name: e.name, type: e.type, isSymlink: e.isSymlink }));
          };
          const resolved = await resolvePathPattern(skillsPath, readdir, { dot: true, maxDepth: 4 });
          // For staleness checks we need directories: matched dirs directly,
          // and parent dirs for matched files (e.g. **/SKILL.md → parent skill dir)
          const dirs = new Set<string>();
          for (const entry of resolved) {
            if (entry.type === 'directory') {
              dirs.add(entry.path);
            } else {
              dirs.add(this.#getParentPath(entry.path));
            }
          }
          const dirList = [...dirs];
          this.#globDirCache.set(skillsPath, dirList);
          this.#globResolveTimes.set(skillsPath, now);
        }
        pathsToCheck = this.#globDirCache.get(skillsPath) ?? [];
      } else {
        pathsToCheck = [skillsPath];
      }

      for (const pathToCheck of pathsToCheck) {
        try {
          const stat = await this.#source.stat(pathToCheck);
          const mtime = stat.modifiedAt.getTime();

          if (mtime > this.#lastDiscoveryTime) {
            return true;
          }

          // Skip subdirectory scan for non-directory paths (direct skill references)
          if (stat.type !== 'directory') {
            continue;
          }

          // If this directory is itself a skill root, check its SKILL.md mtime.
          // This covers direct skill paths and file-level glob expansions (e.g., **/SKILL.md).
          if (this.#checkSkillFileMtime) {
            const directSkillFilePath = this.#joinPath(pathToCheck, 'SKILL.md');
            try {
              const directSkillFileStat = await this.#source.stat(directSkillFilePath);
              if (
                directSkillFileStat.type === 'file' &&
                directSkillFileStat.modifiedAt.getTime() > this.#lastDiscoveryTime
              ) {
                return true;
              }
            } catch {
              // Not a direct skill dir (or SKILL.md unavailable), continue to subdirectory scan.
            }
          }

          // Also check subdirectories (skill directories) for changes — in parallel
          const entries = await this.#source.readdir(pathToCheck);
          const dirEntries = entries.filter(entry => entry.type === 'directory');

          if (dirEntries.length > 0) {
            const statResults = await Promise.all(
              dirEntries.map(async entry => {
                const entryPath = this.#joinPath(pathToCheck, entry.name);
                try {
                  const entryStat = await this.#source.stat(entryPath);
                  if (entryStat.modifiedAt.getTime() > this.#lastDiscoveryTime) {
                    return true;
                  }

                  // Optionally check SKILL.md file mtime - editing file content may not update directory mtime.
                  // This doubles stat calls per skill, so it's opt-in for local development scenarios.
                  if (this.#checkSkillFileMtime) {
                    const skillFilePath = this.#joinPath(entryPath, 'SKILL.md');
                    try {
                      const skillFileStat = await this.#source.stat(skillFilePath);
                      return (
                        skillFileStat.type === 'file' && skillFileStat.modifiedAt.getTime() > this.#lastDiscoveryTime
                      );
                    } catch {
                      // SKILL.md doesn't exist or can't be stat'd, skip
                    }
                  }
                } catch {
                  // Couldn't stat entry, skip it
                }
                return false;
              }),
            );
            if (statResults.some(stale => stale)) {
              return true;
            }
          }
        } catch {
          // Couldn't stat path (doesn't exist or error), skip to next
          continue;
        }
      }
    }

    return false;
  }

  /**
   * Parse a SKILL.md file
   */
  async #parseSkillFile(filePath: string, dirName: string, source: ContentSource): Promise<InternalSkill> {
    const rawContent = await this.#source.readFile(filePath);
    const content = typeof rawContent === 'string' ? rawContent : rawContent.toString('utf-8');

    const parsed = matter(content);
    const frontmatter = parsed.data;
    const body = parsed.content.trim();

    // Extract required fields
    // Get skill directory path (parent of SKILL.md) - needed for SkillMetadata
    const skillPath = this.#getParentPath(filePath);

    const metadata: SkillMetadata = {
      name: frontmatter.name,
      path: skillPath,
      description: frontmatter.description,
      license: frontmatter.license,
      compatibility: frontmatter.compatibility,
      'user-invocable': frontmatter['user-invocable'],
      metadata: frontmatter.metadata,
    };

    // Validate if enabled (includes token/line count warnings)
    if (this.#validateOnLoad) {
      const validation = this.#validateSkillMetadata(metadata, dirName, body);
      if (!validation.valid) {
        throw new Error(`Invalid skill metadata in ${filePath}:\n${validation.errors.join('\n')}`);
      }
    }

    // Discover reference, script, and asset files (parallel — independent subdirs)
    const [references, scripts, assets] = await Promise.all([
      this.#discoverFilesInSubdir(skillPath, 'references'),
      this.#discoverFilesInSubdir(skillPath, 'scripts'),
      this.#discoverFilesInSubdir(skillPath, 'assets'),
    ]);

    // Build indexable content (instructions + references)
    const indexableContent = await this.#buildIndexableContent(body, skillPath, references);

    return {
      ...metadata,
      instructions: body,
      source,
      references,
      scripts,
      assets,
      indexableContent,
    };
  }

  /**
   * Validate skill metadata (delegates to shared validation function)
   */
  #validateSkillMetadata(
    metadata: SkillMetadata,
    dirName: string,
    instructions?: string,
  ): { valid: boolean; errors: string[]; warnings: string[] } {
    const result = validateSkillMetadata(metadata, dirName, instructions);

    // Log warnings if any
    if (result.warnings.length > 0) {
      for (const warning of result.warnings) {
        console.warn(`[WorkspaceSkills] ${metadata.name}: ${warning}`);
      }
    }

    return result;
  }

  /**
   * Discover files in a subdirectory of a skill (references/, scripts/, assets/)
   */
  async #discoverFilesInSubdir(skillPath: string, subdir: 'references' | 'scripts' | 'assets'): Promise<string[]> {
    const subdirPath = this.#joinPath(skillPath, subdir);
    const files: string[] = [];

    if (!(await this.#source.exists(subdirPath))) {
      return files;
    }

    try {
      await this.#walkDirectory(subdirPath, subdirPath, (relativePath: string) => {
        files.push(relativePath);
      });
    } catch {
      // Failed to read subdirectory
    }

    return files;
  }

  /**
   * Walk a directory recursively and call callback for each file.
   * Limited to maxDepth (default 20) to prevent stack overflow on deep hierarchies.
   */
  async #walkDirectory(
    basePath: string,
    dirPath: string,
    callback: (relativePath: string) => void,
    depth: number = 0,
    maxDepth: number = 20,
  ): Promise<void> {
    if (depth >= maxDepth) {
      return;
    }

    const entries = await this.#source.readdir(dirPath);

    for (const entry of entries) {
      const entryPath = this.#joinPath(dirPath, entry.name);

      if (entry.type === 'directory' && !entry.isSymlink) {
        await this.#walkDirectory(basePath, entryPath, callback, depth + 1, maxDepth);
      } else {
        // Get relative path from base
        const relativePath = entryPath.substring(basePath.length + 1);
        callback(relativePath);
      }
    }
  }

  /**
   * Build indexable content from instructions and references
   */
  async #buildIndexableContent(instructions: string, skillPath: string, references: string[]): Promise<string> {
    const parts = [instructions];

    // Read all reference files in parallel (independent reads, order preserved by map)
    const refContents = await Promise.all(
      references.map(async refPath => {
        const fullPath = this.#joinPath(skillPath, 'references', refPath);
        try {
          const rawContent = await this.#source.readFile(fullPath);
          return typeof rawContent === 'string' ? rawContent : rawContent.toString('utf-8');
        } catch {
          return null; // Skip files that can't be read
        }
      }),
    );

    for (const content of refContents) {
      if (content !== null) parts.push(content);
    }

    return parts.join('\n\n');
  }

  /**
   * Remove a skill's entries from the search index.
   */
  async #removeSkillFromIndex(skill: InternalSkill): Promise<void> {
    if (!this.#searchEngine?.remove) return;

    const ids = [`skill:${skill.path}:SKILL.md`, ...skill.references.map(r => `skill:${skill.path}:${r}`)];
    for (const id of ids) {
      try {
        await this.#searchEngine.remove(id);
      } catch {
        // Best-effort removal; entry may already be gone
      }
    }
  }

  /**
   * Infer the ContentSource for a skill path by matching against resolved paths.
   */
  #inferSource(skillPath: string): ContentSource {
    for (const rp of this.#resolvedPaths) {
      if (skillPath === rp || skillPath.startsWith(rp + '/')) {
        return this.#determineSource(rp);
      }
    }
    return this.#determineSource(skillPath);
  }

  /**
   * Index a skill for search
   */
  async #indexSkill(skill: InternalSkill): Promise<void> {
    if (!this.#searchEngine) return;

    // Index the main skill instructions
    await this.#searchEngine.index({
      id: `skill:${skill.path}:SKILL.md`,
      content: skill.instructions,
      metadata: {
        skillPath: skill.path,
        source: 'SKILL.md',
      },
    });

    // Index each reference file in parallel (independent reads + index calls)
    await Promise.all(
      skill.references.map(async refPath => {
        const fullPath = this.#joinPath(skill.path, 'references', refPath);
        try {
          const rawContent = await this.#source.readFile(fullPath);
          const content = typeof rawContent === 'string' ? rawContent : rawContent.toString('utf-8');
          await this.#searchEngine!.index({
            id: `skill:${skill.path}:${refPath}`,
            content,
            metadata: {
              skillPath: skill.path,
              source: `references/${refPath}`,
            },
          });
        } catch {
          // Skip files that can't be read
        }
      }),
    );
  }

  /**
   * Simple text search fallback when no search engine is configured
   */
  async #simpleSearch(query: string, options: SkillSearchOptions): Promise<SkillSearchResult[]> {
    const { topK = 5, skillNames, includeReferences = true } = options;
    const queryLower = query.toLowerCase();
    const results: SkillSearchResult[] = [];

    for (const candidates of this.#skills.values()) {
      // Use tie-break winner for each name
      const skill = await this.#tieBreak(candidates);
      if (!skill) continue;

      // Filter by skill names if specified
      if (skillNames && !skillNames.includes(skill.name)) {
        continue;
      }

      // Search in instructions
      if (skill.instructions.toLowerCase().includes(queryLower)) {
        results.push({
          skillName: skill.name,
          skillPath: skill.path,
          source: 'SKILL.md',
          content: skill.instructions.substring(0, 200),
          score: 1,
        });
      }

      // Search in references if included
      if (includeReferences) {
        for (const refPath of skill.references) {
          if (results.length >= topK) break;
          const content = await this.getReference(skill.name, `references/${refPath}`);
          if (content && content.toLowerCase().includes(queryLower)) {
            results.push({
              skillName: skill.name,
              skillPath: skill.path,
              source: `references/${refPath}`,
              content: content.substring(0, 200),
              score: 0.8,
            });
          }
        }
      }

      if (results.length >= topK) break;
    }

    return results.slice(0, topK);
  }

  /**
   * Determine the source type based on the path
   */
  #determineSource(skillsPath: string): ContentSource {
    // Use path segment matching to avoid false positives (e.g., my-node_modules)
    const segments = skillsPath.split('/');
    if (segments.includes('node_modules')) {
      return { type: 'external', packagePath: skillsPath };
    }
    if (skillsPath.includes('/.mastra/skills') || skillsPath.startsWith('.mastra/skills')) {
      return { type: 'managed', mastraPath: skillsPath };
    }
    return { type: 'local', projectPath: skillsPath };
  }

  /**
   * Join path segments (workspace paths use forward slashes)
   */
  #joinPath(...segments: string[]): string {
    return segments
      .map((seg, i) => (i === 0 ? stripTrailingSlashes(seg) : stripLeadingAndTrailingSlashes(seg)))
      .filter(Boolean)
      .join('/');
  }

  /**
   * Validate and normalize a relative path to prevent directory traversal.
   * Throws if the path contains traversal segments (..) or is absolute.
   */
  #assertRelativePath(input: string, label: string): string {
    const normalized = input.replace(/\\/g, '/');
    const segments = normalized.split('/').filter(seg => Boolean(seg) && seg !== '.');
    if (normalized.startsWith('/') || segments.some(seg => seg === '..')) {
      throw new Error(`Invalid ${label} path: ${input}`);
    }
    return segments.join('/');
  }

  /**
   * Get parent path
   */
  #getParentPath(path: string): string {
    const lastSlash = path.lastIndexOf('/');
    return lastSlash > 0 ? path.substring(0, lastSlash) : '/';
  }
}

function stripTrailingSlashes(s: string): string {
  let end = s.length;
  while (end > 0 && s.charCodeAt(end - 1) === 47 /* "/" */) {
    end--;
  }
  return end === s.length ? s : s.slice(0, end);
}

function stripLeadingAndTrailingSlashes(s: string): string {
  let start = 0;
  while (start < s.length && s.charCodeAt(start) === 47 /* "/" */) {
    start++;
  }
  let end = s.length;
  while (end > start && s.charCodeAt(end - 1) === 47 /* "/" */) {
    end--;
  }
  return start === 0 && end === s.length ? s : s.slice(start, end);
}
